/**
 * getFormIndex returns the index of the form by name.
 *
 * @returns {Number}
 */
export const getFormIndex = state => ({ form }) =>
  state.forms.findIndex(f => f.name === form);

/**
 * formExists returns whether the given form exists or not.
 */
export const formExists = (_, getters) => ({ form }) =>
  getters.getFormIndex({ form }) >= 0;

/**
 * getFields return all fields of the form, or an empty object if not found.
 *
 * @returns {Object}
 */
export const getFields = (state, getters) => ({ form }) => {
  const index = getters.getFormIndex({ form });
  if (index < 0) {
    return {};
  }

  return state.forms[index].fields;
};

/**
 * getField returns a resulting field by it's key in the forms fields.
 *
 * @returns {String|Number|undefined}
 */
export const getField = (state, getters) => ({ form, field }) => {
  const index = getters.getFormIndex({ form });
  if (index < 0 || !_.get(state.forms[index].fields, field)) {
    return undefined;
  }

  return _.get(state.forms[index].fields, field);
};

/**
 * fieldExists returns whether the given field exists in the form.
 */
export const fieldExists = (_, getters) => ({ form, field }) =>
  getField({ form, field }) !== undefined;

/**
 * getErrors returns the errors of a resulting field by it's key in the forms fields.
 *
 * @returns {Array}
 */
export const getErrors = (state, getters) => ({ form, field }) => {
  const index = getters.getFormIndex({ form });
  if (index < 0 || !state.forms[index].errors.hasOwnProperty(field)) {
    return [];
  }

  return state.forms[index].errors[field];
};

/**
 * getStatus returns the current status of the form.
 */
export const getStatus = (state, getters) => ({ form }) => {
  const index = getters.getFormIndex({ form });
  if (index < 0) {
    return "";
  }

  return state.forms[index].status;
};

/**
 * getFieldIsDisabled returns the errors of a resulting field by it's key in the forms fields.
 *
 * @returns {Boolean}
 */
export const getFieldIsDisabled = (state, getters) => ({ form, field }) => {
  const index = getters.getFormIndex({ form });
  if (index < 0 || !state.forms[index].disabled.hasOwnProperty(field)) {
    return false;
  }

  return (Boolean(state.forms[index].disabled[field]) || getters.getStatus({form}) === 'loading');
};
